import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { FadeUp } from "../components/Reveal";

const EMPTY_SVC = { name: "", description: "", base_price: "", unit: "per mix", duration_minutes: 60, category: "individual", deposit_percent: 50, active: true };
const EMPTY_LOC = { name: "", address: "", rate_adjustment: 0, description: "", active: true };

function CrudSection({ title, items, empty, fields, endpoint, onSaved, testPrefix, renderMeta }) {
  const [editing, setEditing] = useState(null); // null | "new" | id
  const [form, setForm] = useState(empty);

  const start = (item) => {
    if (item) {
      const f = {};
      Object.keys(empty).forEach((k) => (f[k] = item[k] ?? empty[k]));
      setForm(f);
      setEditing(item[fields.idKey]);
    } else {
      setForm(empty);
      setEditing("new");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, base_price: form.base_price === "" ? undefined : Number(form.base_price), rate_adjustment: Number(form.rate_adjustment || 0), duration_minutes: Number(form.duration_minutes || 0) };
      if ("deposit_percent" in form) payload.deposit_percent = Math.max(0, Math.min(100, Number(form.deposit_percent === "" ? 50 : form.deposit_percent)));
      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
      if (editing === "new") await api.post(endpoint, payload);
      else await api.put(`${endpoint}/${editing}`, payload);
      toast.success("Saved");
      setEditing(null);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    await api.delete(`${endpoint}/${id}`);
    toast.success("Removed");
    onSaved();
  };

  return (
    <FadeUp className="bg-white rounded-3xl border border-purple-100 p-7" data-testid={`${testPrefix}-section`}>
      <div className="flex items-center justify-between">
        <p className="font-serif text-2xl text-obsidian">{title}</p>
        <button onClick={() => start(null)} data-testid={`${testPrefix}-add`}
          className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-obsidian text-white text-xs hover:text-gold transition-colors">
          <Plus size={13} /> Add
        </button>
      </div>

      {editing && (
        <form onSubmit={save} className="mt-5 grid gap-3 bg-lilac-mist rounded-2xl p-5" data-testid={`${testPrefix}-form`}>
          {fields.inputs.map((inp) =>
            inp.type === "select" ? (
              <select key={inp.key} value={form[inp.key]} onChange={(e) => setForm({ ...form, [inp.key]: e.target.value })} data-testid={`${testPrefix}-input-${inp.key}`}
                className="rounded-xl border border-purple-100 px-3 py-2.5 text-sm bg-white">
                {inp.options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <label key={inp.key} className="grid gap-1">
                {inp.label && <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 pl-1">{inp.label}</span>}
                <input type={inp.type || "text"} placeholder={inp.placeholder} value={form[inp.key]} required={inp.required} min={inp.min} max={inp.max} step={inp.step}
                  onChange={(e) => setForm({ ...form, [inp.key]: e.target.value })} data-testid={`${testPrefix}-input-${inp.key}`}
                  className="rounded-xl border border-purple-100 px-3 py-2.5 text-sm w-full" />
              </label>
            )
          )}
          <div className="flex gap-3">
            <button type="submit" data-testid={`${testPrefix}-save`} className="px-6 py-2.5 rounded-full bg-gold text-obsidian text-xs font-semibold hover:brightness-110 transition-all">Save</button>
            <button type="button" onClick={() => setEditing(null)} data-testid={`${testPrefix}-cancel`} className="text-xs text-slate-400 hover:text-slate-600">Cancel</button>
          </div>
        </form>
      )}

      <div className="mt-5 space-y-2">
        {items.map((item) => {
          const id = item[fields.idKey];
          return (
            <div key={id} className="flex items-center gap-3 p-4 rounded-2xl bg-lilac-mist text-sm" data-testid={`${testPrefix}-row-${id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-obsidian truncate flex items-center gap-2">
                  {fields.icon && <MapPin size={13} className="text-gold shrink-0" />}
                  {item[fields.titleKey]}
                </p>
                <p className="text-xs text-slate-400 mt-0.5 truncate">{renderMeta(item)}</p>
              </div>
              <button onClick={() => start(item)} data-testid={`${testPrefix}-edit-${id}`} className="text-slate-400 hover:text-purple-700 transition-colors" aria-label="Edit"><Pencil size={15} /></button>
              <button onClick={() => remove(id)} data-testid={`${testPrefix}-delete-${id}`} className="text-slate-400 hover:text-red-500 transition-colors" aria-label="Delete"><Trash2 size={15} /></button>
            </div>
          );
        })}
      </div>
    </FadeUp>
  );
}

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [locations, setLocations] = useState([]);

  const load = () => {
    api.get("/services?all=true").then((r) => setServices(r.data)).catch(() => {});
    api.get("/locations?all=true").then((r) => setLocations(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-services">
      <FadeUp>
        <h1 className="font-serif text-3xl sm:text-4xl text-obsidian">Services &amp; <span className="italic text-purple-700">rates</span></h1>
        <p className="text-sm text-slate-500 mt-2">Location adjustments apply on top of every service price. Each service sets its own deposit % — raise it for lock-in packages. No code required.</p>
      </FadeUp>
      <div className="mt-10 grid gap-6 lg:grid-cols-2 items-start">
        <CrudSection
          title="Services"
          testPrefix="service"
          items={services}
          empty={EMPTY_SVC}
          endpoint="/services"
          onSaved={load}
          fields={{
            idKey: "service_id",
            titleKey: "name",
            inputs: [
              { key: "name", placeholder: "Service name", required: true },
              { key: "description", placeholder: "Description" },
              { key: "base_price", placeholder: "Base price ($)", type: "number", required: true },
              { key: "unit", placeholder: "Unit", type: "select", options: ["per mix", "per song", "per hour", "per session", "per month", "per package"] },
              { key: "duration_minutes", placeholder: "Duration (min)", type: "number" },
              { key: "category", placeholder: "Group", type: "select", options: ["individual", "deal", "package"] },
              { key: "deposit_percent", label: "Deposit required to lock in (%)", placeholder: "Deposit % (e.g. 50 or 75)", type: "number", min: 0, max: 100, step: 5 },
            ],
          }}
          renderMeta={(s) => `$${s.base_price} ${s.unit}${s.duration_minutes ? ` · ${s.duration_minutes} min` : ""} · ${s.deposit_percent ?? 50}% deposit`}
        />
        <CrudSection
          title="Locations"
          testPrefix="location"
          items={locations}
          empty={EMPTY_LOC}
          endpoint="/locations"
          onSaved={load}
          fields={{
            idKey: "location_id",
            titleKey: "name",
            icon: true,
            inputs: [
              { key: "name", placeholder: "Location name", required: true },
              { key: "address", placeholder: "Address" },
              { key: "rate_adjustment", placeholder: "Rate adjustment ($, e.g. 25 or -30)", type: "number" },
              { key: "description", placeholder: "Description" },
            ],
          }}
          renderMeta={(l) => `${l.address || "—"} · ${l.rate_adjustment === 0 ? "base rate" : l.rate_adjustment > 0 ? `+$${l.rate_adjustment}` : `-$${Math.abs(l.rate_adjustment)}`}`}
        />
      </div>
    </div>
  );
}
